import { ROUTE_CONFIG } from '../router/routes';
import { getSignedUser } from './userUtils';
import { trackingEvent } from '../analytics/index';
import { getBasicAmplitudEventProperties } from '../analytics/utils';
import { AMPLITUDE_SERVICE } from '../analytics/constants';

/**
 * Manages route tracking and performance monitoring for specific views.
 * This class provides tracking capabilities to measure and report the time
 * it takes for users to navigate between different parts of the application.
 * It implements the Singleton pattern to ensure only one instance exists.
 * @class
 */
class RoutePerformanceTracker {
  /**
   * Gets the singleton instance of RoutePerformanceTracker.
   * @returns {RoutePerformanceTracker} The singleton instance
   */
  static getInstance() {
    if (!RoutePerformanceTracker._instance) {
      RoutePerformanceTracker._instance = new RoutePerformanceTracker();
    }
    return RoutePerformanceTracker._instance;
  }

  /**
   * Creates a new instance of RoutePerformanceTracker.
   * This constructor should not be called directly - use RoutePerformanceTracker.getInstance() instead.
   * @constructor
   */
  constructor() {
    this._routes = {
      masterplan: ROUTE_CONFIG.SCHEDULE.path,
      gantt: '/gantt/:sectorId/ganttchart',
      lookahead: ROUTE_CONFIG.LOOKAHEAD.path
    };

    this._state = {
      navigationStartTime: null,
      viewName: null,
      isGanttTracking: false,
      currentPath: null,
      previousPath: null,
      originPath: null,
      isRefresh: false
    };

    this._viewConfigs = {
      masterplan: {
        name: ROUTE_CONFIG.SCHEDULE.name,
        selectors: ['.gantt_task_content', '.gantt-container .gantt_row_task']
      },
      lookahead: {
        name: ROUTE_CONFIG.LOOKAHEAD.name,
        selectors: ['.gantt_row', '.ant-empty.ant-empty-normal']
      },
      gantt: {
        name: ROUTE_CONFIG.SCHEDULE.name,
        selectors: ['.gantt_task_content', '.gantt-container .gantt_row_task']
      }
    };

    if (typeof window !== 'undefined') {
      if (document.readyState === 'complete') {
        this._initializeTracker();
      } else {
        window.addEventListener('load', () => {
          this._initializeTracker();
        });
      }
    }
  }

  /**
   * Initializes the route tracker by setting up event listeners for history state changes
   * and intercepting history API methods.
   * @private
   */
  _initializeTracker() {
    window.addEventListener('popstate', (event) => {
      const currentPath = window.location.pathname || '';
      const previousPath = event.state?.previousPath || '';

      this._handleRouteChange(currentPath, previousPath);
    });

    this._interceptHistoryMethods();
    this._handleRouteChange(window.location.pathname || '');
  }

  /**
   * Intercepts History API methods to enable SPA route tracking.
   *
   * Technical Rationale:
   * In SPAs, direct History API calls (pushState/replaceState) don't trigger
   * 'popstate' events. This interception is necessary because:
   * 1. React Router uses these methods internally
   * 2. Standard navigation events aren't fired during programmatic navigation
   * 3. We need to capture the previous path before it changes
   *
   * Implementation Notes:
   * - Stores original methods to preserve native functionality
   * - Wraps calls while maintaining the original context via apply()
   * - Injects previousPath into state for origin tracking
   * - Handles null states to prevent TypeError exceptions
   *
   * State Mutation:
   * {
   *   ...originalState,
   *   previousPath: '/current/path' // Injected before navigation
   * }
   *
   * @private
   * @method
   * @see https://developer.mozilla.org/en-US/docs/Web/API/History_API
   */
  _interceptHistoryMethods() {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      const previousPath = window.location.pathname || '';

      if (!args[0]) args[0] = {};
      args[0].previousPath = previousPath;

      originalPushState.apply(window.history, args);

      this._handleRouteChange(args[2] || '', previousPath);
    };

    window.history.replaceState = (...args) => {
      const previousPath = window.location.pathname || '';

      if (!args[0]) args[0] = {};
      args[0].previousPath = previousPath;

      originalReplaceState.apply(window.history, args);

      this._handleRouteChange(args[2] || '', previousPath);
    };
  }

  /**
   * Gets the navigation type using modern Performance API.
   * @private
   * @returns {string} Navigation type: 'reload', 'navigate', 'back_forward', 'prerender'
   */
  _getNavigationType() {
    try {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      return navigationEntry?.type || 'unknown';
    } catch (e) {
      return 'unknown';
    }
  }

  /**
   * Handles route changes by identifying route types, updating state,
   * and starting performance tracking when applicable.
   * @private
   * @param {string} path - The new route path the user is navigating to
   * @param {string} previousPath - The previous route path the user is navigating from
   */
  _handleRouteChange(path = '', previousPath = '') {
    const routeType = this._identifyRouteType(path);

    this._state.previousPath = previousPath;
    this._state.currentPath = path;

    if (!routeType) {
      return;
    }

    this._initializeNavigationTracking(routeType, previousPath);

    this._startViewReadinessCheck(routeType);
  }

  /**
   * Initialize tracking state for a new navigation
   * @private
   * @param {string} routeType - The identified route type
   * @param {string} previousPath - The previous path
   */
  _initializeNavigationTracking(routeType, previousPath) {
    // Only initialize if we haven't started tracking yet
    if (this._state.navigationStartTime) return;

    const navigationType = this._getNavigationType();
    const isPageRefresh = !previousPath && navigationType === 'reload';
    const isDirectAccess = !previousPath && navigationType === 'navigate';
    const isDirectEntry = isPageRefresh || isDirectAccess;

    this._state.originPath = isDirectEntry ? null : previousPath;
    this._state.isRefresh = isDirectEntry;
    this._state.navigationStartTime = performance.now();
    this._state.isGanttTracking =
      routeType === 'masterplan' || routeType === 'gantt';
    this._state.viewName = this._state.isGanttTracking
      ? 'Schedule'
      : 'Lookahead';
  }

  /**
   * Route type resolver for performance tracking.
   *
   * Technical Rationale:
   * Schedule views can be accessed through multiple route patterns:
   * 1. /masterplan (standard route)
   * 2. /gantt/:sectorId/ganttchart (parameterized route)
   * Both need to be tracked as Schedule for analytics consistency.
   *
   * Resolution Priority:
   * 1. Gantt pattern (most specific)
   * 2. Masterplan (exact match)
   * 3. Lookahead (exact match)
   *
   * @private
   * @param {string} path - Current route path
   * @returns {string|null} 'masterplan'|'gantt'|'lookahead'|null
   */
  _identifyRouteType(path) {
    if (!path) return null;
    if (this._matchesGanttPattern(path)) return 'gantt';
    if (path === this._routes.masterplan) return 'masterplan';
    if (path === this._routes.lookahead) return 'lookahead';
    return null;
  }

  /**
   * Checks if a path matches the Gantt route pattern.
   * @private
   * @param {string} path - The path to check
   * @returns {boolean} True if the path matches the Gantt pattern
   */
  _matchesGanttPattern(path) {
    if (!path) return false;
    const pattern = this._routes.gantt
      .replace(/:[\w]+/g, '\\d+')
      .replace(/\//g, '\\/');
    return new RegExp(`^${pattern}$`).test(path);
  }

  /**
   * Starts checking for view readiness by polling DOM elements
   * defined in the view configuration.
   * @private
   * @param {string} viewType - The type of view to check ('masterplan', 'gantt', 'lookahead')
   */
  _startViewReadinessCheck(viewType) {
    const configKey = viewType === 'gantt' ? 'gantt' : viewType;
    const config = this._viewConfigs[configKey];

    if (!config) {
      return;
    }

    const MAX_ATTEMPTS = 6000; // 5 minutes total maximum wait time
    const CHECK_INTERVAL_MS = 50;
    let attempts = 0;
    let checkInterval;

    checkInterval = setInterval(() => {
      attempts++;

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(checkInterval);
        this._resetTrackerState();
        return;
      }

      if (this._isViewReady(viewType, config)) {
        clearInterval(checkInterval);
        this._completePerformanceTracking(config.name);
      }
    }, CHECK_INTERVAL_MS);
  }

  /**
   * Resets the tracker state when tracking is aborted or completed.
   * @private
   */
  _resetTrackerState() {
    const { currentPath } = this._state;

    this._state = {
      navigationStartTime: null,
      viewName: null,
      isGanttTracking: false,
      currentPath,
      previousPath: null,
      originPath: null,
      isRefresh: false
    };
  }

  /**
   * Checks if a view is ready based on its configuration by validating
   * the presence of specific DOM elements.
   * @private
   * @param {string} viewType - The type of view to check
   * @param {Object} config - The view configuration containing selectors
   * @returns {boolean} True if the view is ready
   */
  _isViewReady(viewType, config) {
    // Early exit conditions
    if (!config?.selectors?.length) return false;
    if (this._isLoaderActive()) return false;

    // Check for presence of required elements
    const hasElements = this._hasRequiredElements(config.selectors);

    // Special validation for Gantt views
    if (this._isGanttView(viewType) && hasElements) {
      return this._hasGanttContent();
    }

    return hasElements;
  }

  /**
   * Checks if the global loader is active
   * @private
   * @returns {boolean} True if the loader is active
   */
  _isLoaderActive() {
    return this._state.isGanttTracking && window.loader?.isActive?.();
  }

  /**
   * Checks if any of the required elements exist in the DOM
   * @private
   * @param {string[]} selectors - CSS selectors to check
   * @returns {boolean} True if any selector matches elements
   */
  _hasRequiredElements(selectors) {
    return selectors.some(
      (selector) => selector && document.querySelector(selector) !== null
    );
  }

  /**
   * Checks if the view is a Gantt-type view
   * @private
   * @param {string} viewType - The view type to check
   * @returns {boolean} True if the view is Gantt or masterplan
   */
  _isGanttView(viewType) {
    return viewType === 'gantt' || viewType === 'masterplan';
  }

  /**
   * Verifies that Gantt content is populated
   * @private
   * @returns {boolean} True if Gantt content contains text
   */
  _hasGanttContent() {
    const ganttContent = document.querySelector('.gantt_task_content');
    return ganttContent?.textContent?.trim().length > 0;
  }

  /**
   * Gets the current user role from the application state.
   * @private
   * @returns {string} The user role or 'Unknown' if not available
   */
  _getUserRole() {
    return getSignedUser()?.role || 'Unknown';
  }

  /**
   * Gets activity and link quantities for the current view.
   * @private
   * @param {string} viewType - The type of view ('gantt' or 'lookahead')
   * @returns {Object} Object containing activity and link counts
   */
  _getQuantities(viewType) {
    const ganttInstance = this._getGanttInstance(viewType);
    return this._countGanttEntities(ganttInstance);
  }

  /**
   * Gets the appropriate Gantt instance based on view type
   * @private
   * @param {string} viewType - The view type
   * @returns {Object|null} The Gantt instance or null
   */
  _getGanttInstance(viewType) {
    if (viewType === 'gantt') {
      return window.to_use_react_gantt;
    } else if (viewType === 'lookahead') {
      return window.ganttVisualization;
    }
    return null;
  }

  /**
   * Counts activities and links in a Gantt instance
   * @private
   * @param {Object|null} gantt - The Gantt instance
   * @returns {Object} Object with activity and link counts
   */
  _countGanttEntities(gantt) {
    let activities = 0;
    let links = 0;

    if (gantt) {
      activities = gantt.getTaskByTime()?.length || 0;
      links = gantt.getLinks()?.length || 0;
    }

    return { activities, links };
  }

  /**
   * Formats tracking data for logging and analytics purposes.
   * Combines all relevant metrics into a structured object.
   * @private
   * @param {string} viewName - The name of the view being tracked
   * @param {number} loadTime - Load time in milliseconds
   * @returns {Object} Formatted tracking data
   */
  _formatTrackingData(viewName, loadTime) {
    const viewType = viewName.toLowerCase();
    const { activities, links } = this._getQuantities(
      viewType === 'schedule' ? 'gantt' : viewType
    );

    const originModule = this._state.isRefresh
      ? 'Refresh'
      : this._state.originPath
        ? this._getModuleFromPath(this._state.originPath)
        : 'Unknown';

    const trackingData = {
      timestamp: new Date().toISOString(),
      origin_module: originModule,
      load_time_ms: loadTime,
      user_role: this._getUserRole(),
      activity_quantity: activities
    };

    if (this._state.isGanttTracking) {
      trackingData.links_quantity = links;
    }

    return trackingData;
  }

  /**
   * Finalizes view load tracking and emits analytics event.
   * @fires Analytics#schedule_load_time_tracked | Analytics#lookahead_load_time_tracked
   * @param {string} viewName - Schedule|Lookahead
   * @private
   */
  _completePerformanceTracking(viewName) {
    if (!this._state.navigationStartTime) return;

    const loadTime = Math.round(
      performance.now() - this._state.navigationStartTime
    );
    const trackingData = this._formatTrackingData(viewName, loadTime);

    const eventName = this._state.isGanttTracking
      ? 'schedule_load_time_tracked'
      : 'lookahead_load_time_tracked';

    trackingEvent(
      eventName,
      {
        ...getBasicAmplitudEventProperties(),
        ...trackingData
      },
      AMPLITUDE_SERVICE
    );

    const { currentPath } = this._state;

    this._state = {
      navigationStartTime: null,
      viewName: null,
      isGanttTracking: false,
      currentPath,
      previousPath: null,
      originPath: null,
      isRefresh: false
    };
  }

  /**
   * Extracts and resolves a user-friendly module name from a URL path.
   * This complex method translates raw URL paths into meaningful module names used for analytics.
   *
   * The resolution process follows this sequence:
   * 1. Sanitize the input path by removing trailing slashes
   * 2. Extract the first path segment to identify the primary module
   * 3. Check against direct mappings (fastest path resolution)
   * 4. If no direct match, search through ROUTE_CONFIG for more complex matches
   * 5. Apply special case handling for certain paths (e.g., gantt paths → Schedule)
   *
   * Special cases:
   * - Empty paths return 'Unknown'
   * - '/gantt/...' paths are mapped to 'Schedule' regardless of their specific parameters
   * - Paths with variants (sub-routes) are matched by their base path
   *
   * Examples:
   * - '/dashboard' → 'Dashboard'
   * - '/projects' → 'Projects'
   * - '/masterplan' → 'Schedule'
   * - '/gantt/123/ganttchart' → 'Schedule'
   * - '/lookahead/planification' → 'Lookahead'
   * - '/settings/user' → 'Settings'
   * - '/unknown/path' → 'Unknown'
   *
   * @private
   * @param {string} path - The URL path to process and resolve to a module name
   * @returns {string} The resolved module name or 'Unknown' if no mapping could be determined
   */
  _getModuleFromPath(path) {
    if (!path) return 'Unknown';

    const cleanPath = path.replace(/\/$/, '');
    const pathParts = cleanPath.split('/').filter(Boolean);
    const firstPart = pathParts[0] || '';

    const directModules = {
      dashboard: 'Dashboard',
      projects: 'Projects',
      settings: 'Settings',
      masterplan: 'Schedule',
      gantt: 'Schedule',
      lookahead: 'Lookahead'
    };

    if (directModules[firstPart]) {
      return directModules[firstPart];
    }

    const moduleConfig = Object.values(ROUTE_CONFIG).find((config) => {
      return (
        cleanPath === config.path ||
        (config.variants && cleanPath.startsWith(config.path)) ||
        (firstPart === 'gantt' && config.name === 'Schedule')
      );
    });

    const resolvedModule =
      moduleConfig?.name || directModules[firstPart] || 'Unknown';

    return resolvedModule;
  }
}

/**
 * Singleton instance of the RoutePerformanceTracker.
 * Use this exported instance to interact with route tracking functionality.
 * @type {RoutePerformanceTracker}
 */
export const routeTracker = RoutePerformanceTracker.getInstance();
